// ════════════════════════════════════════════════════════════════════════
// DashboardLayout.tsx — Layout compound: menú lateral + contenido
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: Compound Component Pattern
//   <DashboardLayout.Root>
//     <DashboardLayout.Sidebar>
//       <DashboardLayout.MenuItem to="/dashboard" icon={<BarChartIcon />}>Dashboard</DashboardLayout.MenuItem>
//     </DashboardLayout.Sidebar>
//     <DashboardLayout.Content>...</DashboardLayout.Content>
//   </DashboardLayout.Root>
//
// No necesita Context interno porque no comparte estado entre las piezas —
// solo estructura visual. Aun así se mantiene el mismo estilo de API que
// LoginModal para que todas las piezas del dashboard se sientan consistentes.
// ════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styles from './DashboardLayout.module.css';

// ── Root ────────────────────────────────────────────────────────────────
function Root({ children }: { children: ReactNode }) {
  return <div className={styles.root}>{children}</div>;
}

// ── Sidebar ─────────────────────────────────────────────────────────────
// En escritorio es la columna fija de siempre. En móvil/tablet vertical
// (≤ 768px) se oculta y aparece el botón "Menú del panel": al tocarlo, el
// MISMO contenido del sidebar se abre como panel deslizable desde la
// izquierda. Se cierra al navegar, con Escape o tocando el fondo, así el
// menú de móvil y el de escritorio salen de un único lugar (AppSidebarMenu).
function Sidebar({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const { pathname } = useLocation();

  // Cerrar el panel al cambiar de ruta (el usuario eligió una opción).
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Mientras está abierto: Escape cierra y el fondo no hace scroll.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileOpen]);

  return (
    <>
      <button
        type="button"
        className={styles.mobileMenuButton}
        onClick={() => setMobileOpen(true)}
        aria-expanded={mobileOpen}
        aria-controls="dashboard-sidebar"
      >
        <span className={styles.mobileMenuBars} aria-hidden="true">☰</span>
        Menú del panel
      </button>

      {mobileOpen && (
        <div className={styles.mobileBackdrop} onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}

      <aside
        id="dashboard-sidebar"
        className={`${styles.sidebar} ${mobileOpen ? styles.sidebarOpen : ''}`}
        aria-label="Menú de navegación del dashboard"
      >
        <div className={styles.mobileSidebarHeader}>
          <span>Menú del panel</span>
          <button
            type="button"
            className={styles.mobileCloseButton}
            onClick={() => setMobileOpen(false)}
            aria-label="Cerrar menú"
          >
            ✕
          </button>
        </div>
        {children}
      </aside>
    </>
  );
}

// ── MenuItem ────────────────────────────────────────────────────────────
interface MenuItemProps {
  to: string;
  icon?: ReactNode;  // Antes era un emoji (string) — ahora un ícono SVG (ej: <BarChartIcon />)
  children: ReactNode;
}
function MenuItem({ to, icon, children }: MenuItemProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `${styles.menuItem} ${isActive ? styles.menuItemActive : ''}`}
    >
      {icon && (
        <span className={styles.menuIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </NavLink>
  );
}

// ── ExternalMenuItem ────────────────────────────────────────────────────
// Mismo estilo visual que MenuItem, pero para un link que sale del propio
// sitio (ej. Grafana, en otro dominio) — por eso <a target="_blank"> en vez
// de <NavLink>: no tiene sentido resaltarlo como "activo" ni manejarlo con
// el router interno.
interface ExternalMenuItemProps {
  href: string;
  icon?: ReactNode;
  children: ReactNode;
}
function ExternalMenuItem({ href, icon, children }: ExternalMenuItemProps) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={styles.menuItem}>
      {icon && (
        <span className={styles.menuIcon} aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </a>
  );
}

// ── MenuGroup ───────────────────────────────────────────────────────────
// Grupo colapsable de MenuItem (ej. "7mo 3er Trimestre" con un submenú de
// materias). Se abre automáticamente si alguna ruta hija está activa, para
// que al navegar directo a una materia el grupo no aparezca cerrado.
interface MenuGroupProps {
  label: string;
  icon?: ReactNode;  // Antes era un emoji (string) — ahora un ícono SVG
  defaultOpen?: boolean;
  children: ReactNode;
}
function MenuGroup({ label, icon, defaultOpen = false, children }: MenuGroupProps) {
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  return (
    <div className={styles.menuGroup}>
      <button
        type="button"
        className={styles.menuGroupHeader}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
      >
        {icon && (
          <span className={styles.menuIcon} aria-hidden="true">
            {icon}
          </span>
        )}
        <span className={styles.menuGroupLabel}>{label}</span>
        <span className={`${styles.menuGroupChevron} ${isOpen ? styles.menuGroupChevronOpen : ''}`} aria-hidden="true">
          ▾
        </span>
      </button>
      {isOpen && <div className={styles.menuSubItems}>{children}</div>}
    </div>
  );
}

// ── Content ─────────────────────────────────────────────────────────────
function Content({ children }: { children: ReactNode }) {
  return <main className={styles.content}>{children}</main>;
}

export const DashboardLayout = { Root, Sidebar, MenuItem, ExternalMenuItem, MenuGroup, Content };
